<template>
	<Tabs.Root
		class="tabs"
		:class="`tabs-${variant}`"
		:model-value="modelValue"
		@update:model-value="value => emit('update:modelValue', value)"
		:defaultValue="defaultValue"
		:orientation="orientation"
		:activationMode="activationMode"
		:lazyMount="lazyMount"
		:unmountOnExit="unmountOnExit"
	>
		<Tabs.List>
			<Tabs.Trigger v-for="tab in items" :key="tab.id" :value="tab.id" :disabled="tab.disabled">
				<span>{{ tab.label || $t(`opportunities.tabs.${tab.id}`) || tab.id }}</span>
				<span v-if="tab.count !== undefined" class="tab-count">{{ tab.count }}</span>
			</Tabs.Trigger>
		</Tabs.List>

		<div class="tab-content">
			<Tabs.Content v-for="tab in items" :key="`content-${tab.id}`" :value="tab.id">
				<slot :name="tab.id" :tab="tab" />
			</Tabs.Content>
		</div>
	</Tabs.Root>
</template>

<script setup lang="ts">
import { Tabs } from "@ark-ui/vue"

interface TabItem {
	id: string
	label?: string
	count?: number
	disabled?: boolean
}

interface Props {
	items: TabItem[]
	modelValue?: string
	defaultValue?: string
	variant?: "line" | "pill"
	orientation?: "horizontal" | "vertical"
	activationMode?: "automatic" | "manual"
	lazyMount?: boolean
	unmountOnExit?: boolean
}

withDefaults(defineProps<Props>(), {
	orientation: "horizontal",
	activationMode: "automatic",
	lazyMount: false,
	unmountOnExit: false,
	defaultValue: undefined,
	variant: "line",
})

const emit = defineEmits<{
	"update:modelValue": [value: string]
}>()
</script>

<style scoped>
:deep([data-part="list"]) {
	width: 100%;
	display: flex;
	border-bottom: var(--border-width-thin) solid var(--neutral-200);
	position: relative;
	background: var(--neutral-0);
}

:deep([data-part="trigger"]) {
	background: none;
	border: none;
	padding: 0 var(--spacing-card-padding-xs) var(--spacing-card-padding-xs) var(--spacing-card-padding-xs);
	font-size: var(--font-size-base);
	font-weight: var(--font-weight-medium);
	color: var(--neutral-400);
	cursor: pointer;
	position: relative;
	text-transform: none;
	letter-spacing: normal;
	flex: 1;
}

:deep([data-part="trigger"][aria-selected="true"]) {
	color: var(--primary-green);
	width: 50%;
	border-bottom: var(--border-width-thin) solid var(--primary-green);
	margin-bottom: -1px;
}

.tabs {
	:deep([data-part="trigger"][aria-selected="true"]) {
		color: var(--primary-green);
	}

	[data-part="trigger"]:hover:not([aria-selected="true"]) {
		color: var(--neutral-600);
	}
}

.tabs-pill:deep([data-part="list"]) {
	gap: var(--spacing-sm);
	padding: 0 var(--spacing-xl) var(--spacing-card-padding-sm);
	border-bottom: none;
}

.tabs-pill:deep([data-part="trigger"]) {
	display: inline-flex;
	flex: none;
	align-items: center;
	gap: var(--spacing-sm);
	width: auto;
	padding: var(--spacing-sm) var(--spacing-card-padding-sm);
	border-radius: var(--border-radius-full);
	color: var(--neutral-600);
	font-size: var(--font-size-small);
}

.tabs-pill:deep([data-part="trigger"][aria-selected="true"]) {
	width: auto;
	margin-bottom: 0;
	border-bottom: none;
	background-color: var(--neutral-900);
	color: var(--neutral-0);
}

:deep(.tab-count) {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: var(--spacing-card-padding-md);
	height: var(--spacing-card-padding-md);
	padding: 0 var(--spacing-xxs);
	border-radius: var(--border-radius-full);
	background-color: var(--neutral-200);
	color: var(--neutral-600);
	font-size: var(--font-size-xs);
}

:deep([data-part="trigger"][aria-selected="true"] .tab-count) {
	background-color: var(--accent-highlight-light);
	color: var(--primary-green);
}

.tabs-line:deep(.tab-count) {
	margin-left: var(--spacing-xs);
}
</style>
